import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Crown, User, LogOut } from 'lucide-react';
import { isAuthenticated, getUser, logout, isAdmin } from '../../utils/auth';
import { api } from '../../utils/api';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [brandSettings, setBrandSettings] = useState(null);
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    api.getBrandSettings().then(res => setBrandSettings(res.data)).catch(console.error);
  }, []);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const closeMobile = () => setMobileMenuOpen(false);

  const Logo = () => (
    <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link" onClick={closeMobile}>
      {brandSettings?.primary_logo ? (
        <img src={brandSettings.primary_logo} alt="Crowns by Preshy" className="h-10 lg:h-12" />
      ) : (
        <>
          <Crown className="h-7 w-7 lg:h-8 lg:w-8 text-[#3E2723]" strokeWidth={1.5} />
          <span className="font-serif text-xl lg:text-2xl text-[#3E2723] font-medium">
            {brandSettings?.business_name || 'Crowns by Preshy'}
          </span>
        </>
      )}
    </Link>
  );

  const navLinks = [
    { to: '/', label: 'Home', testId: 'nav-home' },
    { to: '/services', label: 'Services', testId: 'nav-services' },
    { to: '/gallery', label: 'Gallery', testId: 'nav-gallery' },
    { to: '/about', label: 'About', testId: 'nav-about' },
    { to: '/contact', label: 'Contact', testId: 'nav-contact' },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/20" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">

        {/* ===== DESKTOP HEADER ===== */}
        <div className="hidden lg:flex items-center justify-between h-20">
          <Logo />

          <nav className="flex items-center gap-7" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-[#1A1A1A] hover:text-[#3E2723] transition-colors font-medium tracking-wide"
                data-testid={link.testId}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            {isAuthenticated() ? (
              <>
                {isAdmin() ? (
                  <Link
                    to="/admin"
                    className="text-sm text-[#1A1A1A] hover:text-[#3E2723] transition-colors flex items-center gap-1.5 font-medium"
                    data-testid="nav-admin"
                  >
                    <User className="h-4 w-4" />
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    className="text-sm text-[#1A1A1A] hover:text-[#3E2723] transition-colors flex items-center gap-1.5 font-medium"
                    data-testid="nav-dashboard"
                  >
                    <User className="h-4 w-4" />
                    My Account
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="text-sm text-[#786E66] hover:text-[#3E2723] transition-colors flex items-center gap-1.5"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-[#1A1A1A] hover:text-[#3E2723] transition-colors font-medium"
                  data-testid="nav-login"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-[#3E2723] text-white hover:bg-[#2A1A17] text-sm font-medium rounded-full px-6 py-2.5 transition-all hover:-translate-y-0.5"
                  data-testid="nav-signup"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>

        {/* ===== MOBILE HEADER ===== */}
        <div className="lg:hidden flex items-center justify-between h-16">
          <Logo />

          <div className="flex items-center gap-2">
            {isAuthenticated() ? (
              <>
                {isAdmin() ? (
                  <Link
                    to="/admin"
                    className="text-xs text-[#3E2723] font-medium px-3 py-1.5 rounded-full border border-[#E6DCD3] hover:bg-[#F3EBE1] transition-colors"
                    data-testid="mobile-nav-admin"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    className="text-xs text-[#3E2723] font-medium px-3 py-1.5 rounded-full border border-[#E6DCD3] hover:bg-[#F3EBE1] transition-colors"
                    data-testid="mobile-nav-dashboard"
                  >
                    Account
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-xs text-[#3E2723] font-medium px-3 py-1.5 rounded-full border border-[#E6DCD3] hover:bg-[#F3EBE1] transition-colors"
                  data-testid="mobile-nav-login"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="text-xs text-white font-medium bg-[#3E2723] hover:bg-[#2A1A17] px-3 py-1.5 rounded-full transition-colors"
                  data-testid="mobile-nav-signup"
                >
                  Sign Up
                </Link>
              </>
            )}

            <button
              className="p-1.5 ml-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-[#3E2723]" />
              ) : (
                <Menu className="h-6 w-6 text-[#3E2723]" />
              )}
            </button>
          </div>
        </div>

        {/* ===== MOBILE HAMBURGER MENU ===== */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden border-t border-[#E6DCD3] pb-5 pt-3 animate-in slide-in-from-top-2 duration-200"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMobile}
                  className="text-[#1A1A1A] hover:text-[#3E2723] hover:bg-[#F3EBE1] rounded-lg px-4 py-3 text-sm font-medium transition-colors"
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              ))}

              {isAuthenticated() && (
                <>
                  <div className="border-t border-[#E6DCD3] my-2"></div>
                  <button
                    onClick={handleLogout}
                    className="text-left text-[#786E66] hover:text-[#3E2723] hover:bg-[#F3EBE1] rounded-lg px-4 py-3 text-sm font-medium flex items-center gap-2 transition-colors"
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
